<script setup lang="ts">
import { ref, computed, unref, watch } from 'vue';
import { isEmpty, isNil } from 'lodash';
import {
  convertBytes,
  FileUnits,
  getDocumentNameFromUrl,
  MULTI_DOC_MAX_FILES_PER_APPOINTMENT
} from '@nova/core';
import { useHttpClient } from '@satellite/composables/useHttpClient';
import { isHttpErrorWithMessage } from './types';

import type {
  DocumentUploadButtonEmits,
  DocumentUploadButtonProps,
  StorageResponse,
  FailedUpload,
  UploadResult
} from './types';

const props = withDefaults(defineProps<DocumentUploadButtonProps>(), {
  warehouseId: '',
  storageEndpoint: '',
  modelValue: undefined,
  multiple: false,
  variant: 'default',
  readOnly: false,
  label: 'Upload file',
  placeholder: 'Choose file...',
  conversionUnit: 'KB',
  maxSize: undefined,
  maxFiles: undefined
});

const emit = defineEmits<DocumentUploadButtonEmits>();

const httpClient = useHttpClient();
const fileInput = ref<File | File[] | null>(null);
const loading = ref(false);
const errorMessages = ref<string[]>([]);
/** Files that failed to upload; kept so user can retry without re-selecting. */
const failedFiles = ref<File[]>([]);

const acceptedMimeTypes = computed(() => props.allowedMimeTypes?.join(',') ?? undefined);

/**
 * The field's value is the uploaded key in `modelValue`, not the file input's contents: the input
 * is cleared before each upload and the key renders as a chip. Vuetify hands rules the input's
 * value, so each rule is rebound to `modelValue`, or a required field would fail after uploading
 * and a field that was already filled would fail from the start.
 */
const modelRules = computed(() => (props.rules ?? []).map(rule => () => rule(props.modelValue)));

const fileInputRef = ref<{ resetValidation: () => Promise<void> } | null>(null);

/**
 * Vuetify re-validates when the input's own value changes, which `modelValue` never is, so an
 * upload, a clear or a chip removal would leave the last result on screen. Resetting re-runs the
 * rules silently and drops a visible error; a form submit shows it again if it still applies.
 */
watch(
  () => props.modelValue,
  () => fileInputRef.value?.resetValidation()
);

const conversionUnitEnum = computed((): FileUnits => {
  const u = (props.conversionUnit ?? 'KB').toUpperCase();
  return u === 'MB' ? FileUnits.MB : FileUnits.KB;
});

/** The chip truncates with CSS to the space it has, and its tooltip needs the whole name. */
const FULL_FILE_NAME_LENGTH = Infinity;

/** Matches the exact /public/storage path segment (public upload endpoint template). */
const PUBLIC_STORAGE_PATH_REGEX = /\/public\/storage(?:\/|$)/u;

const storageBase = computed(() => (props.storageEndpoint ?? '').replace(/\/$/u, ''));

const isPublicStorageEndpoint = computed(() => PUBLIC_STORAGE_PATH_REGEX.test(storageBase.value));

function getUploadUrl(): string {
  const base = storageBase.value;
  if (!base) {
    return '';
  }

  if (isPublicStorageEndpoint.value) {
    if (!props.warehouseId) {
      throw new Error('warehouseId is required for public storage endpoint');
    }
    const baseWithoutStorage = base.replace(/\/storage\/?$/u, '');
    return `${baseWithoutStorage}/${props.warehouseId}/storage`;
  }

  // Authenticated endpoint (e.g. /storage): use as-is
  return base;
}

function validateFileSize(file: File): boolean {
  if (isNil(props.maxSize) || props.maxSize <= 0) {
    return true;
  }
  const sizeInUnit = convertBytes(file.size, conversionUnitEnum.value);
  if (sizeInUnit > props.maxSize) {
    const unit = props.conversionUnit ?? 'KB';
    errorMessages.value = [`File size exceeds limit of ${props.maxSize} ${unit}`];
    return false;
  }
  return true;
}

function validateFileType(file: File): boolean {
  if (!props.allowedMimeTypes?.length) {
    return true;
  }
  const type = (file.type ?? '').toLowerCase();
  const allowed = props.allowedMimeTypes.map(m => m.toLowerCase());
  const isAllowed = allowed.some(
    m => type === m || (m.endsWith('*') && type.startsWith(m.slice(0, -1)))
  );
  if (!isAllowed) {
    errorMessages.value = ['File type is not allowed'];
    return false;
  }
  return true;
}

function validateMaxFiles(files: File[]): boolean {
  if (!props.multiple || files.length === 0) {
    return true;
  }
  const existingCount = Array.isArray(props.modelValue) ? props.modelValue.length : 0;
  const maxAllowed = props.maxFiles ?? MULTI_DOC_MAX_FILES_PER_APPOINTMENT;
  if (existingCount + files.length > maxAllowed) {
    errorMessages.value = [`Maximum of ${maxAllowed} files allowed`];
    return false;
  }
  return true;
}

function getErrorMessage(err: unknown): string {
  const error = err instanceof Error ? err : new Error(String(err));
  if (isHttpErrorWithMessage(err)) {
    const msg = err.response?.data?.message;
    if (typeof msg === 'string') {
      return msg;
    }
  }
  return error.message ?? 'Upload failed';
}

async function uploadFile(file: File): Promise<string> {
  const url = getUploadUrl();
  if (!url) {
    throw new Error('Upload URL not configured');
  }

  const formData = new FormData();
  formData.append('file', file);

  const response = await httpClient.post<StorageResponse>(url, formData);

  const data = response.data;
  if (!data?.key) {
    throw new Error('Invalid response: missing key');
  }
  return data.key;
}

async function uploadFileBatch(files: File[]): Promise<string[]> {
  const url = getUploadUrl();
  if (!url) {
    throw new Error('Upload URL not configured');
  }

  const formData = new FormData();
  files.forEach(file => formData.append('files', file));

  const response = await httpClient.post<StorageResponse[]>(`${url}/files`, formData);

  const keys = (response.data ?? []).map(storedFile => storedFile?.key);
  if (keys.length !== files.length || keys.some(isEmpty)) {
    throw new Error('Invalid response: missing key');
  }
  return keys;
}

async function runBatchUpload(fileList: File[]): Promise<UploadResult> {
  try {
    return { keys: await uploadFileBatch(fileList), failed: [] };
  } catch (error) {
    const message = getErrorMessage(error);
    return { keys: [], failed: fileList.map(file => ({ file, error: message })) };
  }
}

async function runUploads(fileList: File[]): Promise<UploadResult> {
  if (fileList.length > 1 && !isPublicStorageEndpoint.value) {
    return runBatchUpload(fileList);
  }

  const uploadPromises = fileList.map(file => uploadFile(file));
  const results = await Promise.allSettled(uploadPromises);

  const keys: string[] = [];
  const failed: FailedUpload[] = [];

  results.forEach((result, index) => {
    const file = fileList[index];
    if (result.status === 'fulfilled') {
      keys.push(result.value);
    } else {
      failed.push({ file, error: getErrorMessage(result.reason) });
    }
  });

  return { keys, failed };
}

function buildFailedUploadMessage(failed: FailedUpload[]): string {
  if (failed.length === 0) {
    return '';
  }
  if (failed.length === 1) {
    return failed[0].error;
  }
  const failedNames = failed.map(f => f.file.name).join(', ');
  return `${failed.length} file(s) could not be uploaded (${failedNames}). Please try again.`;
}

function reportFailedUploads(failed: FailedUpload[]): void {
  const message = buildFailedUploadMessage(failed);
  errorMessages.value = [message];
  emit('error', new Error(message));
}

async function handleFileSelect(files: File | File[] | null) {
  fileInput.value = files;
  errorMessages.value = [];
  failedFiles.value = [];

  if (!files) {
    emit('update:model-value', props.multiple ? [] : '');
    emit('cleared');
    return;
  }

  const fileList = Array.isArray(files) ? files : [files];

  if (fileList.length === 0) {
    fileInput.value = null;
    emit('update:model-value', props.multiple ? [] : '');
    return;
  }

  if (!validateMaxFiles(fileList)) {
    fileInput.value = null;
    emit('error', new Error(errorMessages.value[0]));
    return;
  }

  for (const file of fileList) {
    if (!validateFileSize(file) || !validateFileType(file)) {
      fileInput.value = null;
      emit('error', new Error(errorMessages.value[0]));
      return;
    }
  }

  loading.value = true;
  emit('uploading');
  fileInput.value = null;

  const { keys, failed } = await runUploads(fileList);

  if (keys.length > 0) {
    if (props.multiple) {
      const existing = Array.isArray(props.modelValue) ? props.modelValue : [];
      emit('update:model-value', [...existing, ...keys]);
    } else {
      emit('update:model-value', keys[0]);
    }
  }

  if (failed.length > 0) {
    failedFiles.value = failed.map(f => f.file);
    reportFailedUploads(failed);
  }

  if (failed.length === 0) {
    emit('uploaded');
  }

  loading.value = false;
}

async function handleRetryFailed() {
  const toRetry = failedFiles.value;
  if (toRetry.length === 0) {
    return;
  }

  loading.value = true;
  emit('uploading');
  errorMessages.value = [];

  const { keys, failed } = await runUploads(toRetry);

  if (keys.length > 0) {
    if (props.multiple) {
      const existing = Array.isArray(props.modelValue) ? props.modelValue : [];
      emit('update:model-value', [...existing, ...keys]);
    } else {
      emit('update:model-value', keys[0]);
    }
  }

  failedFiles.value = failed.map(f => f.file);

  if (failed.length > 0) {
    reportFailedUploads(failed);
  } else {
    emit('uploaded');
  }

  loading.value = false;
}

function handleClear() {
  fileInput.value = null;
  errorMessages.value = [];
  failedFiles.value = [];
  emit('update:model-value', props.multiple ? [] : '');
  emit('cleared');
}

function handleRemove(index: number) {
  if (Array.isArray(props.modelValue)) {
    const newValue = [...props.modelValue];
    newValue.splice(index, 1);
    emit('update:model-value', newValue);
  }
}
</script>

<template>
  <div class="document-upload-button d-flex flex-column ga-2">
    <v-file-input
      ref="fileInputRef"
      color="primary"
      density="comfortable"
      hide-details="auto"
      variant="outlined"
      validate-on="submit"
      :accept="unref(acceptedMimeTypes)"
      :disabled="unref(loading)"
      :error-messages="unref(errorMessages)"
      :label="label ?? undefined"
      :model-value="unref(fileInput)"
      :multiple="multiple"
      :placeholder="placeholder"
      :readonly="readOnly"
      :rules="modelRules"
      @update:model-value="handleFileSelect"
      @click:clear="handleClear">
      <template v-if="loading" #append>
        <v-progress-circular indeterminate size="24" width="2" />
      </template>
    </v-file-input>

    <!-- Retry failed uploads (only failed files are re-uploaded) -->

    <v-btn
      v-if="unref(failedFiles).length > 0 && !unref(loading)"
      class="align-self-start"
      color="primary"
      density="comfortable"
      size="small"
      variant="outlined"
      :disabled="readOnly"
      @click="handleRetryFailed">
      Retry {{ unref(failedFiles).length }} failed file{{
        unref(failedFiles).length === 1 ? '' : 's'
      }}
    </v-btn>

    <!-- Preview: existing file keys (display as filename from key or key) -->
    <div v-if="modelValue" class="document-upload-button__chips d-flex flex-wrap ga-1">
      <template v-if="!multiple && typeof modelValue === 'string'">
        <v-chip
          label
          closable
          class="document-upload-button__chip"
          density="comfortable"
          size="small"
          variant="flat"
          :disabled="readOnly"
          @click:close="handleClear">
          <span class="text-truncate pointer-events-auto" tabindex="0">
            {{ getDocumentNameFromUrl(modelValue, FULL_FILE_NAME_LENGTH) }}
            <v-tooltip activator="parent" location="top">
              {{ getDocumentNameFromUrl(modelValue, FULL_FILE_NAME_LENGTH) }}
            </v-tooltip>
          </span>

          <template #close>
            <v-icon icon="$close" size="14"></v-icon>
          </template>
        </v-chip>
      </template>

      <template v-else-if="multiple && Array.isArray(modelValue)">
        <v-chip
          v-for="(fileKey, index) in modelValue"
          :key="index"
          closable
          label
          class="document-upload-button__chip"
          density="comfortable"
          size="small"
          variant="flat"
          :disabled="readOnly"
          @click:close="handleRemove(index)">
          <span class="text-truncate pointer-events-auto" tabindex="0">
            {{ getDocumentNameFromUrl(fileKey, FULL_FILE_NAME_LENGTH) }}
            <v-tooltip activator="parent" location="top">
              {{ getDocumentNameFromUrl(fileKey, FULL_FILE_NAME_LENGTH) }}
            </v-tooltip>
          </span>

          <template #close>
            <v-icon icon="$close" size="14"></v-icon>
          </template>
        </v-chip>
      </template>
    </div>
  </div>
</template>

<style scoped>
.document-upload-button__chips {
  contain: inline-size;
}

.document-upload-button__chip :deep(.v-chip__content) {
  min-width: 0;
}
</style>
